import { Link, useParams } from "react-router-dom";
import ShareButton from "@/components/feature/ShareButton";
import EpisodeCard from "@/components/feature/EpisodeCard";
import { formatDate, getCharacterById, getEpisodeById, getRelatedEpisodes } from "@/utils/content";

export default function EpisodeDetail() {
  const { id } = useParams();
  const episode = getEpisodeById(id);

  if (!episode) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该剧集</h1>
        <p className="mt-2 text-sm text-foreground-600">该剧集资料可能已调整，请返回剧集列表重新浏览。</p>
        <Link
          to="/episodes"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          返回剧集列表
        </Link>
      </div>
    );
  }

  const cast = episode.characters
    .map((cid) => getCharacterById(cid))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));
  const related = getRelatedEpisodes(episode, 4);

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/episodes" className="hover:text-primary-600">剧集全集</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">第 {episode.number} 集</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">
                  第 {episode.number} 集
                </span>
                <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  {episode.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-bookmark-line" />
                    {episode.arc}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    {episode.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {formatDate(episode.airDate)}
                  </span>
                </div>
              </div>
              <ShareButton title={`火影忍者剧集：第 ${episode.number} 集 ${episode.title}`} />
            </div>

            <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={episode.cover}
                alt={`${episode.title} 剧集封面`}
                title={`火影忍者剧集 ${episode.number} ${episode.title}`}
                className="h-64 w-full object-cover md:h-80"
              />
            </div>

            <section className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情介绍</h2>
              <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
                {episode.summary}
              </p>
              <div className="mt-4 space-y-4">
                {episode.story.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 12)}
                    className="text-sm leading-loose text-foreground-700 md:text-base"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">主要出场角色</h2>
              <ul className="mt-4 space-y-3">
                {cast.map((character) => (
                  <li key={character.id}>
                    <Link
                      to={`/character/${character.id}`}
                      className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                    >
                      <span className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-background-200">
                        <img
                          src={character.avatar}
                          alt={`${character.name} 头像`}
                          title={`火影忍者角色 ${character.name}`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {character.name}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">
                          {character.epithet}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">篇章导航</h2>
              <p className="mt-2 text-sm text-foreground-600">
                本集属于「{episode.arc}」，前往剧集列表可查看同一篇章的其它剧集。
              </p>
              <Link
                to="/episodes"
                className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                浏览全部剧集
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-links-line" />
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">相关剧集推荐</h2>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((item) => (
                <EpisodeCard key={item.id} episode={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}